import { useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import AuthContext from "../context/authContext/AuthContext";
import type { AuthContextType } from "../context/authContext/types";

/**
 * Fires a visit beacon to /api/analytics/track/ on each SPA route change.
 *
 * Uses the AuthContext axios instance rather than navigator.sendBeacon so the
 * JWT Authorization header rides along and the backend can attribute the
 * visit to the logged-in user; anonymous visits still record (AllowAny).
 * Consecutive repeats of the same path are deduped; failures are swallowed —
 * analytics must never surface as a user-facing error.
 */
const useTrackPageView = (): void => {
  const auth = useContext(AuthContext) as AuthContextType | undefined;
  const api = auth?.api;
  const { pathname } = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (!api || lastPath.current === pathname) return;
    const referrer = lastPath.current ?? document.referrer ?? "";
    lastPath.current = pathname;
    api
      .post("/api/analytics/track/", { path: pathname, referrer })
      .catch(() => {
        /* ignore */
      });
  }, [pathname, api]);
};

export default useTrackPageView;
